<script>
import Card from '../../../Components/Card.vue';
import TextInput from '../../../Components/Inputs/TextInput.vue';
import Tooltip from '@/Components/Tooltip.vue';

export default {
  components: { Tooltip, TextInput, Card },

  props: {
    shops: Array,
    apiKey: String,
  },

  emits: ['selected'],

  data() {
    return {
      search: null,
      markers: [],
      userLocation: null,
      userMarker: null,
      isSearchingPostal: false,
    };
  },

  computed: {
    filteredShops() {
      let shops = this.shops.filter((shop) => {
        return shop.kiosk_key !== null && shop.kiosk_key != 0 && !shop.hide_from_scheduler;
      });

      if (this.search && this.search.length > 0 && !this.isSearchingPostal) {
        const query = this.search;

        shops = shops.filter((shop) => {
          // ✅ Use whatever fields you actually have (examples below)
          const addressParts = [
            shop.address, // if you have it
            shop.address1, // common
            shop.address2,
            shop.city,
            shop.state,
            shop.postal_code, // or zip
            shop.zip,
            shop.country,
          ].filter(Boolean);

          const haystack = [shop.shop_name, shop.public_name, addressParts.join(' ')]
            .filter(Boolean)
            .join(' ');

          return this.matchesTokens(haystack, query);
        });
      }

      shops = shops.map((shop) => ({
        ...shop,
        _distance: this.calculateDistance(shop),
      }));

      shops.sort((a, b) => {
        if (a._distance === null && b._distance === null) return 0;
        if (a._distance === null) return 1;
        if (b._distance === null) return -1;
        return a._distance - b._distance;
      });

      return shops;
    },
  },

  mounted() {
    if (!window.google?.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=` + this.apiKey;
      script.async = true;
      script.defer = true;
      script.onload = this.initMap;
      document.head.appendChild(script);
    } else {
      this.initMap();
    }
  },

  methods: {
    normalizeSearch(str) {
      return (str ?? '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ') // punctuation -> space
        .replace(/\s+/g, ' ')
        .trim();
    },

    matchesTokens(haystack, query) {
      const h = this.normalizeSearch(haystack);
      const tokens = this.normalizeSearch(query).split(' ').filter(Boolean);

      // require every token to appear somewhere
      return tokens.every((t) => h.includes(t));
    },

    selectShop(shop) {
      this.$emit('selected', shop);
    },

    isPostalCode(value) {
      const usZip = /^\d{5}$/.test(value.trim());
      const caPostal = /^[A-Za-z]\d[A-Za-z][\s]?\d[A-Za-z]\d$/.test(value.trim());
      return usZip || caPostal;
    },

    async geocodePostalCode(postalCode) {
      try {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: postalCode }, async (results, status) => {
          if (status === 'OK' && results.length > 0) {
            const { lat, lng } = results[0].geometry.location;
            this.userLocation = null;
            await this.$nextTick();
            this.userLocation = { lat: lat(), lng: lng() }; // note: lat/lng are functions in the JS SDK
            this.placeUserMarker();
            this.fitBounds();
          }
        });
      } catch (e) {
        // Geocoding failed, just ignore
      }
    },

    /**
     * Haversine formula — returns distance in miles, or null if coordinates are missing.
     */
    calculateDistance(shop) {
      if (!this.userLocation || shop.latitude === null || shop.longitude === null) {
        return null;
      }

      // Distance doesn't apply for hour-based shops
      if (shop.mileage_spelling?.toLowerCase() === 'hours') {
        return null;
      }

      const toRad = (val) => (val * Math.PI) / 180;
      const R = shop.mileage_spelling?.toLowerCase() === 'kilometer' ? 6371 : 3958.8;

      const dLat = toRad(parseFloat(shop.latitude) - this.userLocation.lat);
      const dLng = toRad(parseFloat(shop.longitude) - this.userLocation.lng);

      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(this.userLocation.lat)) *
          Math.cos(toRad(parseFloat(shop.latitude))) *
          Math.sin(dLng / 2) ** 2;

      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    formatDistance(shop) {
      if (shop.mileage_spelling?.toLowerCase() === 'hours') return null;
      if (!this.userLocation) return null;
      if (shop.latitude === null || shop.longitude === null) return 'N/A';
      if (shop._distance === null) return null;

      const unit = shop.mileage_spelling?.toLowerCase() === 'kilometer' ? 'km' : 'mi';
      return `${shop._distance.toFixed(1)} ${unit}`;
    },

    locateUser() {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.placeUserMarker();
          this.fitBounds();
        },
        (err) => {
          console.warn('Geolocation error', err.code, err.message);
          // err.code:
          // 1 = PERMISSION_DENIED
          // 2 = POSITION_UNAVAILABLE
          // 3 = TIMEOUT
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    },

    fitBounds() {
      const bounds = new google.maps.LatLngBounds();

      // Include user location
      bounds.extend(this.userLocation);

      // Include all shop markers
      this.markers.forEach((marker) => bounds.extend(marker.getPosition()));

      this.map.fitBounds(bounds);
    },

    placeUserMarker() {
      if (this.userMarker) {
        this.userMarker.setMap(null);
      }

      this.userMarker = new google.maps.Marker({
        position: this.userLocation,
        map: this.map,
        title: 'Your Location',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
    },

    centerPoint() {
      const shop = this.filteredShops.find(
        (shop) => shop.latitude !== null && shop.longitude !== null,
      );

      if (!shop) return { lat: 0, lng: 0 };
      return { lat: parseFloat(shop.latitude), lng: parseFloat(shop.longitude) };
    },

    initMap() {
      const center = this.centerPoint();

      this.map = new google.maps.Map(document.getElementById('shop-map'), {
        center,
        zoom: 10,
        mapTypeControl: false,
      });

      this.renderMarkers(this.filteredShops);
    },

    renderMarkers(shops) {
      this.markers.forEach((marker) => marker.setMap(null));
      this.markers = [];
      const infoWindow = new google.maps.InfoWindow();

      shops.forEach((shop) => {
        if (shop.latitude === null || shop.longitude === null) return;

        const marker = new google.maps.Marker({
          position: { lat: parseFloat(shop.latitude), lng: parseFloat(shop.longitude) },
          map: this.map,
          title: shop.shop_name,
        });

        marker.addListener('click', () => {
          infoWindow.setContent(`${shop.shop_name}<br>${shop.address}`);
          infoWindow.open(this.map, marker);
        });

        this.markers.push(marker);
      });
    },
  },

  watch: {
    search(value) {
      if (value && this.isPostalCode(value)) {
        this.isSearchingPostal = true;
        this.geocodePostalCode(value);
      } else {
        this.isSearchingPostal = false;
      }
    },
  },
};
</script>

<template>
  <Card class="mt-3">
    <div class="row no-gutters">
      <!-- Left Panel: Search and Shop List -->
      <div class="col-md-5 p-3 border-right">
        <div class="form-group mb-3 d-flex align-items-center gap-2">
          <TextInput
            class="flex-grow-1"
            placeholder="Search for Shop, City or Postal Code"
            :model-value="search"
            @keyup:model-value="(newValue) => (search = newValue)"
          />

          <button
            type="button"
            class="btn btn-outline-primary text-nowrap"
            style="min-width: 40px"
            @click="locateUser"
          >
            <Tooltip tooltip="Use your current location">
              <i class="fa-solid fa-location-crosshairs"></i>
            </Tooltip>
          </button>
        </div>

        <div class="list-group list-group-flush">
          <template v-for="shop in filteredShops" :key="shop.id">
            <a
              href="#"
              class="list-group-item list-group-item-action"
              @click.prevent="selectShop(shop)"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="font-weight-bold">{{ shop.public_name || shop.shop_name }}</div>
                <small v-if="formatDistance(shop)" class="text-muted ml-2 text-nowrap">
                  {{ formatDistance(shop) }}
                </small>
              </div>
              <div>{{ shop.address }}</div>
            </a>
          </template>
        </div>
      </div>

      <!-- Right Panel: Google Map -->
      <div class="col-md-7 p-3">
        <div class="d-flex justify-content-end mb-2"></div>
        <div id="shop-map" style="width: 100%; height: 400px"></div>
      </div>
    </div>
  </Card>
</template>
